import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createDraft, draftStatus, formatList, parseList, publishSnapshot, resetDraft, updateFragment,
} from '@braidlabs/console';
import type { Draft } from '@braidlabs/console';
import { diffRegistries } from '@braidlabs/registry';
import type { FragmentManifest } from '@braidlabs/gateway';
import { ApiCallError, call, consoleApiFor } from '@/api.ts';
import type { FragmentDraft, HeadState } from '@/api.ts';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { Button } from '@/components/ui/button.tsx';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.tsx';
import { EmptyState } from '@/components/ui/empty-state.tsx';
import { Input, Label } from '@/components/ui/input.tsx';
import { Segmented } from '@/components/ui/segmented.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import {
  accessLosses, describeAccessLosses, findingsFor, releaseImpact, severityCounts,
} from '@/lib/registry-findings.ts';
import { buildRegistryRows, STATE_COPY } from '@/lib/registry-states.ts';
import type { RegistryState } from '@/lib/registry-states.ts';
import { refusal, roleRefusal } from '@/lib/refusal.ts';
import { canWrite, useAuthenticated } from '@/session.tsx';

const CHANNELS = ['live', 'beta', 'preview'] as const;

/**
 * The registry: edit the draft, read the findings, and see what publishing would change.
 *
 * **First-party, replacing the OSS `RegistryEditor` (ADR 0005 supersedes §7 of the makeover doc).**
 * The library models two places a manifest can live — a browser-held draft and a published
 * snapshot — and says so deliberately: "drafts are deliberately not server state". True of the OSS
 * gateway, false here. Braid Cloud stores per-fragment `FRAG` drafts, so a manifest has three
 * values at once, and an editor that can only show two of them will sooner or later publish an
 * edit nobody in the room has seen.
 *
 * Everything that is not that distinction is still the library's: `createDraft`, `draftStatus`,
 * `updateFragment`, `resetDraft` and `publishSnapshot` from `@braidlabs/console`, findings, diff
 * and the access matrix from `@braidlabs/registry`. The validation, the diff, and the access
 * preview are the OSS product and reimplementing them would leave two behaviours to keep in step.
 */
export function RegistryPage() {
  const session = useAuthenticated();
  const writable = canWrite(session.role);
  const cannotPublish = roleRefusal(session.role, 'publish snapshots', writable);

  const [draft, setDraft] = useState<Draft | null>(null);
  /** What the control plane has saved, which is not necessarily what this browser is editing. */
  const [saved, setSaved] = useState<FragmentManifest[]>([]);
  const [headId, setHeadId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [channel, setChannel] = useState<string>('live');
  const [confirming, setConfirming] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [head, drafts] = await Promise.all([
      call<HeadState>(session, '/registry/head'),
      call<{ items: FragmentDraft[] }>(session, '/fragments'),
    ]);
    const published = head.snapshot?.manifests ?? [];
    const savedManifests = drafts.items.map((item) => item.manifest);

    setHeadId(head.id);
    setSaved(savedManifests);
    // `createDraft` fixes `base` — what the diff is measured against — to the published snapshot,
    // because "3 changes against live" is the question the publish panel answers. The manifests
    // being edited start from the control plane's saved drafts instead, which is the state an
    // operator is actually continuing from and the one the OSS shape has no room for.
    setDraft({ ...createDraft(published, head.id), manifests: savedManifests.map((m) => ({ ...m })) });
  }, [session]);

  useEffect(() => {
    let live = true;
    load().catch((cause: Error) => { if (live) setError(cause.message); });
    return () => { live = false; };
  }, [load]);

  const view = useMemo(() => {
    if (!draft) return null;
    const state = draftStatus(draft);
    const rows = buildRegistryRows(draft.manifests, saved, draft.base);
    const losses = accessLosses(draft.base, draft.manifests);
    return {
      state,
      rows,
      impact: releaseImpact(state.diff),
      lossSentence: describeAccessLosses(losses),
      counts: severityCounts(state.findings),
    };
  }, [draft, saved]);

  /**
   * Publishing sends the manifests this page is showing, explicitly.
   *
   * `POST /registry/snapshots` with no body publishes *the org's current drafts* (§6.2). That is
   * the wrong call from here: the saved drafts may contain a colleague's edit this browser never
   * displayed, and it would ship inside a snapshot the operator believes they read. Sending the
   * list on screen makes what-you-see and what-you-publish the same set.
   */
  async function publish() {
    if (!draft) return;
    setBusy(true);
    setStatus(null);
    try {
      const outcome = await publishSnapshot(consoleApiFor(session), { manifests: draft.manifests });
      const snapshotId = outcome.snapshot.id;

      // Pointing a channel is a separate act from minting the snapshot (ADR 0007), and it is the
      // one that changes what visitors compose. Kept separate here for the same reason.
      const current = await call<{ items: { name: string; version: number }[] }>(session, '/channels');
      const target = current.items.find((item) => item.name === channel);
      await call(session, `/channels/${channel}`, {
        method: 'PUT',
        body: JSON.stringify({
          snapshotId,
          ...(target ? { expectedVersion: target.version } : {}),
        }),
      });

      setConfirming(false);
      setStatus({
        tone: 'ok',
        text: `Published ${snapshotId} and pointed ${channel} at it. Visitors pick it up within about fifteen seconds.`,
      });
      await load();
    } catch (cause) {
      setStatus({ tone: 'error', text: describe(cause) });
    } finally {
      setBusy(false);
    }
  }

  function edit(id: string, patch: Partial<FragmentManifest>) {
    if (!draft) return;
    const index = draft.manifests.findIndex((manifest) => manifest.id === id);
    if (index === -1) return;
    setDraft(updateFragment(draft, index, patch));
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load the registry</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="grid gap-6">
      <header className="grid gap-2">
        <h1 className="text-[34px] leading-tight font-semibold tracking-[-0.02em]">Registry</h1>
        <p className="max-w-[62ch] text-sm leading-relaxed">
          Branch from what is pinned, edit, then publish. Publishing mints a new immutable snapshot;
          pointing a channel at it is a separate act, and rolling back is re-pointing the channel.
        </p>
      </header>

      {status ? (
        <div
          className={
            status.tone === 'ok'
              ? 'border-l-[3px] border-[color:var(--color-primary)] bg-[color:var(--color-accent)] px-3.5 py-3'
              : 'border-l-[3px] border-[color:var(--color-destructive)] bg-[color:rgba(214,0,108,0.08)] px-3.5 py-3'
          }
        >
          <p className="text-[13.5px] leading-relaxed">{status.text}</p>
        </div>
      ) : null}

      <div className="grid gap-8 lg:grid-cols-[1fr_400px] lg:gap-11">
        {/* Left: what the draft holds. */}
        <div className="grid min-w-0 content-start gap-3">
          {!view ? (
            <div className="grid gap-3">
              {[0, 1, 2].map((row) => <Skeleton key={row} className="h-4 w-full" />)}
            </div>
          ) : view.rows.length === 0 ? (
            <EmptyState
              title="Nothing in the registry"
              description="Register a fragment to compose something. Until then this organization's shells render their own chrome and nothing else."
            />
          ) : (
            <>
              <div className="grid grid-cols-[150px_1fr_130px_60px] gap-3 border-b border-[color:var(--color-foreground)] pb-2 text-[11px] font-medium tracking-[0.12em] uppercase">
                <span>Fragment</span>
                <span>Endpoint</span>
                <span>State</span>
                <span />
              </div>

              {view.rows.map((row) => {
                const rowFindings = findingsFor(view.state.findings, row.id);
                const open = expanded === row.id;
                return (
                  <div key={row.id} className="grid gap-2">
                    <div
                      className={`grid grid-cols-[150px_1fr_130px_60px] items-baseline gap-3 border-b border-[color:var(--color-border)] py-3 ${
                        rowFindings.length > 0 ? 'bg-[color:rgba(214,0,108,0.05)]' : ''
                      }`}
                    >
                      <span className="font-mono text-[13px] font-semibold">{row.id}</span>
                      <span className="text-muted-foreground truncate font-mono text-[12px]" title={endpointOf(row.manifest)}>
                        {endpointOf(row.manifest)}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <StateTag state={row.state} />
                        {/* Somebody else's edit, sitting on the control plane. It is not a state —
                            it can be true of a row in any state — so it is its own mark. */}
                        {row.divergedFromSaved ? (
                          <span
                            className="text-destructive text-[11px]"
                            title="The control plane holds a different version of this fragment than the one shown. Publishing sends what is shown."
                          >
                             diverged
                          </span>
                        ) : null}
                      </span>
                      <button
                        type="button"
                        className="text-primary justify-self-end text-[13px] underline-offset-4 hover:underline"
                        onClick={() => setExpanded(open ? null : row.id)}
                      >
                        {open ? 'Close' : 'Edit'}
                      </button>
                    </div>

                    {rowFindings.length > 0 ? (
                      <div className="grid gap-1 pb-1">
                        {rowFindings.map((finding) => (
                          <p key={finding.code} className="max-w-[70ch] text-[13.5px] leading-relaxed">
                            <Badge variant={finding.severity === 'error' ? 'destructive' : 'secondary'} className="mr-2">
                              {finding.severity}
                            </Badge>
                            {finding.message}
                            {finding.hint ? <span className="text-muted-foreground"> {finding.hint}</span> : null}
                          </p>
                        ))}
                      </div>
                    ) : null}

                    {open ? (
                      // A form framed by a row border reads as a mistake, so the row becomes a card.
                      <div className="bg-card grid gap-4 p-4 sm:grid-cols-2">
                        <FieldRow
                          label="Endpoint"
                          id={`${row.id}-endpoint`}
                          value={endpointOf(row.manifest)}
                          hint="Where the gateway fetches this fragment."
                          disabled={!writable}
                          reason={cannotPublish}
                          onChange={(value) => edit(row.id, { endpoint: value } as Partial<FragmentManifest>)}
                        />
                        <FieldRow
                          label="Pierce patterns"
                          id={`${row.id}-pierce`}
                          value={formatList(row.manifest.pierce)}
                          hint="Comma separated. Which page URLs this fragment claims — gateway-owned."
                          disabled={!writable}
                          reason={cannotPublish}
                          onChange={(value) => edit(row.id, { pierce: parseList(value) } as Partial<FragmentManifest>)}
                        />
                        <p className="text-muted-foreground text-[12.5px] leading-relaxed sm:col-span-2">
                          {STATE_COPY[row.state].meaning}
                        </p>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Right: the publish panel. */}
        <aside className="grid content-start gap-5">
          {view ? (
            <>
              <h2 className="text-[25px] leading-tight font-semibold tracking-[-0.01em]">
                {changeCount(view.impact)} against live
              </h2>

              {view.state.clean ? (
                <p className="text-muted-foreground text-[13px] leading-relaxed">
                  This draft is identical to what is published. There is nothing to release.
                </p>
              ) : (
                <ul className="grid gap-1.5">
                  {view.state.diff.added.map((manifest) => (
                    <ChangeLine key={`+${manifest.id}`} marker="+" id={manifest.id} />
                  ))}
                  {view.state.diff.changed.map((entry) => (
                    <ChangeLine key={`~${entry.id}`} marker="~" id={entry.id} />
                  ))}
                  {view.state.diff.removed.map((manifest) => (
                    <ChangeLine key={`-${manifest.id}`} marker="−" id={manifest.id} />
                  ))}
                </ul>
              )}

              <div className="grid gap-1.5 border-t border-[color:var(--color-border)] pt-5">
                <h3 className="text-[11px] font-medium tracking-[0.12em] uppercase">Release impact</h3>
                <p className="text-[15px] leading-relaxed">
                  <strong>{view.impact.added}</strong> added, <strong>{view.impact.changed}</strong>{' '}
                  changed, <strong>{view.impact.removed}</strong> withdrawn.
                </p>
                <p className="text-muted-foreground text-[13px] leading-relaxed">
                  {view.impact.gatewayOwned} gateway-owned {view.impact.gatewayOwned === 1 ? 'field' : 'fields'}{' '}
                  changed — routing and access, with page-wide blast radius — and {view.impact.appOwned}{' '}
                  the fragment reports about itself.
                </p>
                {view.lossSentence ? (
                  <p className="border-l-[3px] border-[color:var(--color-destructive)] bg-[color:rgba(214,0,108,0.07)] mt-1 px-3 py-2 text-[13px] leading-relaxed">
                    {view.lossSentence} A loss shows up in the diff above as one changed line.
                  </p>
                ) : null}
              </div>

              <div className="grid gap-2 border-t border-[color:var(--color-border)] pt-5">
                <Label htmlFor={`publish-channel-${channel}`}>Point at</Label>
                <Segmented
                  name="publish-channel"
                  value={channel}
                  disabled={!writable}
                  options={CHANNELS.map((name) => ({ value: name, label: name }))}
                  onChange={setChannel}
                />

                <Button
                  className="mt-2 w-full"
                  disabled={!writable || view.state.blocked || view.state.clean || busy}
                  title={refusal(
                    cannotPublish,
                    view.state.blocked
                      ? `${view.counts.errors} ${view.counts.errors === 1 ? 'error' : 'errors'} must be fixed before this can be published.`
                      : null,
                    view.state.clean ? 'Nothing differs from what is already published.' : null,
                  )}
                  onClick={() => setConfirming(true)}
                >
                  Publish
                </Button>

                {/*
                  Two ways back, because there are three states to go back *to*.

                  The OSS `resetDraft` returns to `base`, which is the published snapshot — right
                  when a draft is browser-only, wrong here. An operator's edits sit on top of the
                  control plane's saved drafts, so "discard mine" has to mean "back to what the
                  team saved", not "back to what is live". Offering only `resetDraft` under the
                  label "Discard edits" would quietly throw away colleagues' saved work, and the
                  page would then read "0 changes against live" beside rows marked unsaved.
                */}
                {draft && !sameManifests(draft.manifests, saved) ? (
                  <Button
                    variant="ghost"
                    className="w-full"
                    disabled={busy}
                    onClick={() => setDraft({ ...draft, manifests: saved.map((m) => ({ ...m })) })}
                  >
                    Discard my edits
                  </Button>
                ) : null}

                {draft && !sameManifests(saved, draft.base) ? (
                  <Button
                    variant="ghost"
                    className="text-destructive w-full"
                    disabled={busy}
                    title="Drops the saved drafts too, including any this browser did not write."
                    onClick={() => setReverting(true)}
                  >
                    Revert to published
                  </Button>
                ) : null}

                <p className="text-muted-foreground text-[12.5px] leading-relaxed">
                  A snapshot is immutable and content-addressed: publishing mints a new one rather
                  than changing the old. Visitors compose from it once the fifteen-second config
                  cache expires. Rolling back is pointing the channel at the previous id.
                </p>
              </div>
            </>
          ) : null}
        </aside>
      </div>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Publish and point ${channel} at it?`}
        description={
          <>
            {view ? changeCount(view.impact) : 'The changes'} will be minted as a new snapshot, and{' '}
            <code className="font-mono">{channel}</code> will serve it.
            {view?.lossSentence ? ` ${view.lossSentence}` : ''}
            {headId ? ' Rolling back is re-pointing the channel at the previous id.' : ''}
          </>
        }
        confirmLabel="Publish"
        busy={busy}
        onConfirm={() => void publish()}
      />

      <ConfirmDialog
        open={reverting}
        onOpenChange={setReverting}
        title="Revert to what is published?"
        description={
          'This drops every saved draft as well as your own edits, including drafts somebody else '
          + 'saved and this browser never showed. Nothing is written to the control plane — the '
          + 'saved drafts stay where they are until the next save or publish.'
        }
        confirmLabel="Revert to published"
        destructive
        onConfirm={() => {
          if (draft) setDraft(resetDraft(draft));
          setReverting(false);
        }}
      />
    </div>
  );
}

/** Set equality over manifests, by the same diff the snapshot id is derived through. */
function sameManifests(a: readonly FragmentManifest[], b: readonly FragmentManifest[]): boolean {
  return diffRegistries(a, b).identical;
}

function ChangeLine({ marker, id }: { marker: string; id: string }) {
  const tone = marker === '+'
    ? 'text-primary'
    : marker === '−'
      ? 'text-destructive'
      : 'text-muted-foreground';
  return (
    <li className="grid grid-cols-[20px_1fr] items-baseline gap-1 text-[13px]">
      <span className={`${tone} font-mono font-semibold`} aria-hidden="true">{marker}</span>
      <span className="font-mono">
        <span className="sr-only">{marker === '+' ? 'added' : marker === '−' ? 'removed' : 'changed'}: </span>
        {id}
      </span>
    </li>
  );
}

function StateTag({ state }: { state: RegistryState }) {
  const variant = state === 'removed'
    ? 'destructive'
    : state === 'live'
      ? 'success'
      : 'secondary';
  return (
    <Badge variant={variant} title={STATE_COPY[state].meaning}>
      {STATE_COPY[state].label}
    </Badge>
  );
}

function FieldRow({
  label, id, value, hint, disabled, reason, onChange,
}: {
  label: string;
  id: string;
  value: string;
  hint: string;
  disabled: boolean;
  reason: string | null;
  onChange(value: string): void;
}) {
  return (
    <div className="grid content-start gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        value={value}
        disabled={disabled}
        title={refusal(reason)}
        className="font-mono text-[13px]"
        onChange={(event) => onChange(event.target.value)}
      />
      <p className="text-muted-foreground text-[12.5px] leading-relaxed">{hint}</p>
    </div>
  );
}

function endpointOf(manifest: FragmentManifest): string {
  return typeof manifest.endpoint === 'string' ? manifest.endpoint : '[fetcher function]';
}

function changeCount(impact: { added: number; changed: number; removed: number }): string {
  const total = impact.added + impact.changed + impact.removed;
  return `${total} ${total === 1 ? 'change' : 'changes'}`;
}

function describe(cause: unknown): string {
  return cause instanceof ApiCallError ? `${cause.code}: ${cause.message}` : String(cause);
}
